import { formatDate } from '@angular/common';
import { inject, Injectable, Injector, ViewChild } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ACL_WTAX_LINE_COLUMNS, ColumnOptions, TransactionType } from '@core';
import { STColumn, STComponent, STPage } from '@delon/abc/st';
import { ACLService } from '@delon/acl';
import { _HttpClient, SettingsService } from '@delon/theme';
import { catchError, EMPTY, Subject, Observable, from, throwError } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { GUID_EMPTY } from 'src/app/core/constants';
import { DocNavService } from 'src/app/core/util/doc-nav.service';
import { SalesDocTransferService } from 'src/app/shared-feature/_providers/sales-doc-transfer.service';
import { DocBaseService } from 'src/app/shared-feature/services/_base/doc-base.service';
import { EinvValidatorsService } from 'src/app/shared-feature/services/einv-validators.service';
import { AttachmentChangePayload } from 'src/app/shared/models/attachment-change.model';
import { StockQuantityControlService } from 'src/app/shared/services/stock-quantity-control.service';
import { ValidPostingPeriodCheckingService } from 'src/app/shared/services/valid-posting-period-checking.service';

@Injectable()
export class SinvService extends DocBaseService {
  private einvValidators = inject(EinvValidatorsService);
  constructor(injector: Injector) {
    super(injector);
    this.aclKey = 'sinv';

    this.parentMenuAclKey = 'sales';
    this.transactionType = TransactionType.SINV;
  }

  qtyWasModify: boolean = false;
  customer: any;
  private newLineAddedSubject = new Subject<void>();
  newLineAdded$ = this.newLineAddedSubject.asObservable();

  private componentRef: any;
  setComponentRef(ref: any) {
    this.componentRef = ref;
  }

  override getAttachmentChangesPayload(): AttachmentChangePayload | null {
    if (this.componentRef && this.componentRef.collectAttachmentChanges) {
      return this.componentRef.collectAttachmentChanges();
    }
    return null;
  }

  override clearAttachmentChanges(): void {
    if (this.componentRef && this.componentRef.clearAttachmentChanges) {
      this.componentRef.clearAttachmentChanges();
    }
  }

  get acl() {
    return this.injector.get(ACLService);
  }

  get transferSrv() {
    return this.injector.get(SalesDocTransferService);
  }

  get vppSrv() {
    return this.injector.get(ValidPostingPeriodCheckingService);
  }

  get sqcSrv() {
    return this.injector.get(StockQuantityControlService);
  }

  get docNav() {
    return this.injector.get(DocNavService);
  }

  form = this.createForm();

  updatingRate = false;
  paymentInfoItems: any[] = [];
  paymentInfoLoading = false;
  dimmedDelete = false;

  public get itemDetails() {
    return this.form.controls.itemDetails;
  }

  get einvSubmitted() {
    // E-Invoice is already submitted, or validated, or cancelled
    if (
      this._data.uin &&
      this._data.eInvoiceStatus &&
      (this._data.eInvoiceStatus === 'Valid' ||
        this._data.eInvoiceStatus === 'Submitted' ||
        this._data.eInvoiceStatus === 'Cancelled')
    )
      return true;

    // Assigned to consolidated einvoice
    if (this._data.consolidatedDocCode) return true;
    return false;
  }

  onEInvoiceStatusChange(newStatus: string) {
    this._data.eInvoiceStatus = newStatus;
    this.form.controls.eInvoiceStatus.setValue(newStatus);

    // Synchronize isCancelled
    this._data.isCancelled = newStatus === 'Cancelled';
  }

  // Add this property to store the validation result
  private _isTaxDateValid: boolean = false;
  // Add a flag to prevent multiple simultaneous requests
  private isValidating = false;

  validateTaxDate() {
    // Return if validation is already in progress
    if (this.isValidating) {
      return;
    }

    this.isValidating = true;

    const requestBody = {
      taxDate: this.form.controls.taxDate.value,
      itemDetails: this.form.controls.itemDetails.value || []
    };

    this.http
      .post<{
        isValid: boolean;
      }>('api/sstreturns/ValidateTaxDate?_silent=true', requestBody)
      .subscribe({
        next: response => {
          this._isTaxDateValid = response.isValid;
          this.isValidating = false;
        },
        error: error => {
          this._isTaxDateValid = false;
          this.isValidating = false;
        }
      });
  }

  // Add this getter to access the validation result
  get isTaxDateValid(): boolean {
    return this._isTaxDateValid;
  }

  get displayEInvReq() {
    return this.form?.invalid === true;
  }

  public get isTransferred() {
    if (
      this.itemDetails.value.find(o => o.transferFromQuotationDetailId) ||
      this.itemDetails.value.find(o => o.transferFromSalesOrderDetailId) ||
      this.itemDetails.value.find(o => o.transferFromDeliveryOrderDetailId)
    )
      return true;
    return false;
  }

  public get isGovBirCat(): boolean {
    const cust = this.form.controls.customer.value;
    if (cust) {
      return cust.isGovBirCategory;
    }
    return false;
  }

  private static readonly govWvatColumnNames = [
    'wVatCode',
    'wVatRate',
    'wVatAmount'
  ] as const;

  /**
   * Sync WVAT column visibility with BIR gov category: show when isGovBirCat is true,
   * hide and persist when false (overrides saved column-chooser prefs for those fields).
   */
  applyGovWvatColumnVisibility(layoutId: string): boolean {
    let changed = false;
    if (this.isGovBirCat) {
      for (const name of SinvService.govWvatColumnNames) {
        const col = this.columns.find(c => c.name === name);
        if (
          col &&
          (!col.acl || this.acl.canAbility(col.acl)) &&
          !col.visible
        ) {
          col.visible = true;
          changed = true;
        }
      }
      if (changed) {
        this.persistGovWvatColumnsInLayout(layoutId, true);
      }
    } else {
      for (const name of SinvService.govWvatColumnNames) {
        const col = this.columns.find(c => c.name === name);
        if (
          col &&
          (!col.acl || this.acl.canAbility(col.acl)) &&
          col.visible
        ) {
          col.visible = false;
          changed = true;
        }
      }
      if (changed) {
        this.persistGovWvatColumnsInLayout(layoutId, false);
      }
    }
    return changed;
  }

  private persistGovWvatColumnsInLayout(
    layoutId: string,
    visible: boolean
  ): void {
    if (!layoutId) {
      return;
    }
    const settings = this.injector.get(SettingsService);
    const saved: Array<{ name: string; visible: boolean }> =
      settings.getData(layoutId);
    if (!saved?.length) {
      return;
    }
    const map = new Map(saved.map(s => [s.name, s]));
    for (const name of SinvService.govWvatColumnNames) {
      const col = this.columns.find(c => c.name === name);
      if (col && (!col.acl || this.acl.canAbility(col.acl))) {
        map.set(name, { name, visible });
      }
    }
    settings.setData(layoutId, Array.from(map.values()));
  }

  get hideZipCodeField() {
    // for MY only
    return this.isEInvEnabled && this.form.controls.doCountry.value !== 'MY';
  }

  get isDoAddressTabRequired() {
    if (!this.isEInvEnabled) {
      return false;
    }

    return (
      this.form.get('identificationNo')?.hasError('required') ||
      this.form.get('doZipCode')?.hasError('required') ||
      this.form.get('doCity')?.hasError('required') ||
      // || this.form.get('doState')?.hasError('required')
      // || this.form.get('doCountry')?.hasError('required')
      this.form.get('dotinNo')?.hasError('required')
    );
  }

  get isEInvInfoTabRequired() {
    if (!this.isEInvEnabled) {
      return false;
    }
    return (
      this.form.get('detailsTaxExemption')?.hasError('required') ||
      this.form.get('tinNumber')?.hasError('required')
    );
  }

  get allowEinvValidate() {
    const docDate = new Date(this._data.docDate);
    const einvEffectiveDate = new Date(this.einvEffectiveDate);
    return (
      this.isEInvEnabled &&
      this.acl.can('sinv.einv') &&
      docDate >= einvEffectiveDate
    );
  }

  columns: ColumnOptions[] = [
    {
      key: 'stock',
      name: 'stock',
      title: 'Stock',
      width: '150px',
      align: 'left',
      visible: true
    },
    {
      key: 'desc',
      name: 'desc',
      title: 'Description',
      width: '240px',
      align: 'left',
      visible: true
    },
    {
      key: 'fDesc',
      name: 'fDesc',
      title: 'F. Desc',
      width: '80px',
      align: 'center',
      visible: true
    },
    {
      key: 'qty',
      name: 'qty',
      title: 'Qty',
      width: '120px',
      align: 'right',
      visible: true
    },
    {
      key: 'uom',
      name: 'uom',
      title: 'UOM',
      width: '120px',
      align: 'left',
      visible: true
    },
    {
      key: 'unitPrice',
      name: 'unitPrice',
      title: 'Unit Price',
      width: '120px',
      align: 'right',
      visible: true
    },
    {
      key: 'discount',
      name: 'discount',
      title: {
        text: 'Discount',
        optionalHelp:
          'Discounts can either be a number or a percentage. For percentage discounts, include the "%" symbol.'
      },
      width: '120px',
      align: 'right',
      visible: true
    },
    {
      key: 'discountAmount',
      name: 'discountAmount',
      title: 'Discount Amt.',
      width: '120px',
      align: 'right',
      visible: false
    },
    {
      key: 'amount',
      name: 'amount',
      title: 'Amount',
      width: '120px',
      align: 'right',
      visible: false
    },
    {
      key: 'ref1',
      name: 'ref1',
      title: 'Num Ref 1',
      width: '120px',
      align: 'left',
      visible: false
    },
    {
      key: 'ref2',
      name: 'ref2',
      title: 'Num Ref 2',
      width: '120px',
      align: 'left',
      visible: false
    },
    {
      key: 'dateRef1',
      name: 'dateRef1',
      title: 'Date Ref 1',
      width: '120px',
      align: 'left',
      visible: false
    },
    {
      key: 'dateRef2',
      name: 'dateRef2',
      title: 'Date Ref 2',
      width: '120px',
      align: 'left',
      visible: false
    },
    {
      key: 'classification',
      name: 'classification',
      title: 'Classification',
      width: '150px',
      align: 'left',
      acl: { ability: ['country.MY', 'm1.8388608'], mode: 'allOf' },
      visible: true
    },
    {
      key: 'taxCode',
      name: 'taxCode',
      title: 'Tax',
      width: '120px',
      align: 'left',
      acl: { ability: ['m1.512'], mode: 'allOf' },
      visible: true
    },
    {
      key: 'taxAmount',
      name: 'taxAmount',
      title: 'Tax Amount',
      width: '120px',
      align: 'right',
      acl: { ability: ['m1.512'], mode: 'allOf' },
      visible: false
    },
    {
      key: 'taxExemptionRate',
      name: 'taxExemptionRate',
      title: 'Exempted Rate',
      width: '120px',
      align: 'left',
      acl: { ability: ['m1.512'], mode: 'allOf' },
      visible: false
    },
    {
      key: 'taxExemptionAmount',
      name: 'taxExemptionAmount',
      title: 'Exempted Tax Amount',
      width: '120px',
      align: 'right',
      acl: { ability: ['m1.512'], mode: 'allOf' },
      visible: false
    },
    {
      key: 'taxAmountAdjustment',
      name: 'taxAmountAdjustment',
      title: 'Tax Adjustment',
      width: '120px',
      align: 'right',
      acl: { ability: ['m1.512'], mode: 'allOf' },
      visible: false
    },
    {
      key: 'rentalLeaseExemptedRate',
      name: 'rentalLeaseExemptedRate',
      title: 'Rental/Lease Exempted Rate',
      width: '120px',
      align: 'left',
      acl: { ability: ['country.MY', 'm1.512'], mode: 'allOf' },
      visible: false
    },
    {
      key: 'rentalLeaseExemptedTaxAmount',
      name: 'rentalLeaseExemptedTaxAmount',
      title: 'Rental/Lease Exempted Tax Amount',
      width: '120px',
      align: 'right',
      acl: { ability: ['country.MY', 'm1.512'], mode: 'allOf' },
      visible: false
    },
    {
      key: 'wTaxCode',
      name: 'wTaxCode',
      title: 'W/Tax',
      width: '120px',
      align: 'left',
      acl: ACL_WTAX_LINE_COLUMNS,
      visible: true
    },
    {
      key: 'wTaxAmount',
      name: 'wTaxAmount',
      title: 'W/Tax Amount',
      width: '120px',
      align: 'right',
      acl: ACL_WTAX_LINE_COLUMNS,
      visible: false
    },
    {
      key: 'wTaxAmountAdjustment',
      name: 'wTaxAmountAdjustment',
      title: 'W/Tax Adjustment',
      width: '120px',
      align: 'right',
      acl: ACL_WTAX_LINE_COLUMNS,
      visible: false
    },
    {
      key: 'wVatCode',
      name: 'wVatCode',
      title: 'WVAT',
      width: '120px',
      align: 'left',
      acl: { ability: ['country.PH', 'm2.16'], mode: 'allOf' },
      isGov: true,
      visible: false
    },
    {
      key: 'wVatRate',
      name: 'wVatRate',
      title: 'WVAT Rate',
      width: '120px',
      align: 'right',
      acl: { ability: ['country.PH', 'm2.16'], mode: 'allOf' },
      isGov: true,
      visible: false
    },
    {
      key: 'wVatAmount',
      name: 'wVatAmount',
      title: 'WVAT Amount',
      width: '120px',
      align: 'right',
      acl: { ability: ['country.PH', 'm2.16'], mode: 'allOf' },
      isGov: true,
      visible: false
    },
    {
      key: 'netAmount',
      name: 'netAmount',
      title: 'Net Amount',
      width: '120px',
      align: 'right',
      visible: false
    },
    {
      key: 'project',
      name: 'project',
      title: 'Project',
      width: '120px',
      align: 'left',
      acl: { ability: ['m1.256'] },
      visible: false
    },
    {
      key: 'stockLocation',
      name: 'stockLocation',
      title: 'Location',
      width: '120px',
      acl: { ability: ['m1.8', 'm1.64'], mode: 'allOf' },
      visible: false
    },
    {
      key: 'taxExemptionNo',
      name: 'taxExemptionNo',
      title: 'Tax Exempt No.',
      width: '120px',
      align: 'left',
      acl: { ability: ['country.MY', 'm1.8388608'], mode: 'allOf' },
      visible: false
    },
    {
      key: 'taxExemptionDetails',
      name: 'taxExemptionDetails',
      title: 'Tax Exempt Details',
      width: '200px',
      align: 'left',
      visible: false,
      acl: { ability: ['country.MY', 'm1.8388608'], mode: 'allOf' }
    },
    {
      key: 'tariffCode',
      name: 'tariffCode',
      title: 'Tariff Code',
      width: '120px',
      align: 'left',
      visible: false,
      acl: { ability: ['country.MY', 'm1.8388608'], mode: 'allOf' }
    },
    {
      key: 'account',
      name: 'account',
      title: 'GL Account',
      width: '240px',
      align: 'left',
      visible: false
    },
    {
      key: 'accountName',
      name: 'accountName',
      title: 'GL Account Name',
      width: '240px',
      align: 'left',
      visible: false
    },
    {
      key: 'referenceNo',
      name: 'referenceNo',
      title: 'Ref. No',
      width: '240px',
      align: 'left',
      visible: false
    },
    {
      key: 'stockCategoryCode',
      name: 'stockCategoryCode',
      title: 'Stock Category',
      width: '120px',
      align: 'left',
      visible: false
    },
    {
      key: 'stockGroupCode',
      name: 'stockGroupCode',
      title: 'Stock Group',
      width: '120px',
      align: 'left',
      visible: false
    },
    {
      key: 'stockClassCode',
      name: 'stockClassCode',
      title: 'Stock Class',
      width: '120px',
      align: 'left',
      visible: false
    },
    {
      key: 'stockAlternateName',
      name: 'stockAlternateName',
      title: 'Stock Alternate Name',
      width: '120px',
      align: 'left',
      visible: false
    },
    {
      key: 'minPrice',
      name: 'minPrice',
      title: 'Min Price',
      width: '120px',
      align: 'right',
      visible: false
    },
    {
      key: 'lastSellingPrice',
      name: 'lastSellingPrice',
      title: 'Last Selling Price',
      width: '120px',
      align: 'right',
      visible: false
    },
    {
      key: 'lastPurchasePrice',
      name: 'lastPurchasePrice',
      title: 'Last Purchase Price',
      width: '120px',
      align: 'right',
      visible: false
    }
  ];

  @ViewChild('st') st!: STComponent;
  paymentInfoColumns: STColumn[] = [
    { index: 'paymentDate', title: 'Date', width: 100, render: 'dateTpl' },
    {
      index: 'paymentDocCode',
      title: 'OR #',
      width: 200,
      render: 'doccodeTpl'
    },
    // { index: 'eInvoicePaymentMode', title: 'Payment Mode', width: 200, render: 'paymTpl' },
    { index: 'paymentTerms', title: 'Term', width: 150 },
    { index: 'accountNumber', title: 'Bank Account No.', width: 200 },
    { index: 'paymentRefNo', title: 'Ref. No', width: 200 },
    {
      index: 'paymentAmount',
      title: 'Amount',
      width: 150,
      render: 'amtTpl',
      className: 'text-right'
    }
  ];
  page: STPage = {
    front: false,
    show: false,
    toTop: false
  };

  public get visibleColumns() {
    return this.columns.filter(
      col =>
        (!col.acl || this.acl.canAbility(col.acl)) &&
        (!col.isGov || col.isGov === this.isGovBirCat) &&
        col.visible
    );
  }

  clone() {
    if (
      this.form.controls.isDeferredTax.value &&
      this.form.controls.enableDeferredVat.value === false
    ) {
      this.msg.error(
        'Cannot clone Deferred VAT transaction while the Deferred VAT option is turned off. Please enable option to continue'
      );
      return;
    }

    let obj = { ...this._data };
    delete obj.id;
    obj.id = GUID_EMPTY;
    obj.docCode = '';
    obj.docDate = new Date();
    obj.postDate = new Date();
    obj.taxDate = new Date();
    if (obj.isPast) {
      const newdate = new Date(
        this.cutoffDate.getFullYear(),
        this.cutoffDate.getMonth(),
        0
      );
      obj.docDate = newdate;
      obj.taxDate = newdate;
      obj.postDate = newdate;
    }
    obj.isCancelled = false;
    obj.cancelledDate = null;
    obj.cancelledRemark = null;
    obj.eInvoiceStatus = null;
    obj.uin = null;
    obj.peppolStatus = null;
    obj.consolidatedUIN = null;
    obj.consolidatedDocCode = null;
    obj.transferFrom = null;
    obj.depositId = null;
    obj.depositGroupId = null;
    obj.depositInfo = null;

    if (this.parkAndPost) obj.documentStatus = 0;

    obj.itemDetails.forEach((item: any) => {
      delete item.transferFromQuotationDetailId;
      delete item.transferFromSalesOrderDetailId;
      delete item.transferFromDeliveryOrderDetailId;
      item.transferFromQuotationDetail = null;
      item.transferFromSalesOrderDetail = null;
      item.transferFromDeliveryOrderDetail = null;
      item.transferredQty = 0;
    });

    return obj;
  }

  new() {
    const qtId = this.injector.get(ActivatedRoute).snapshot.queryParams['qt'];
    if (qtId) {
      return this.http.post('api/salestransfers/TransferQtToSinv', {
        _silent: true,
        id: qtId
      });
    }
    const soId = this.injector.get(ActivatedRoute).snapshot.queryParams['so'];
    if (soId) {
      return this.http.post('api/salestransfers/TransferSoToSinv', {
        _silent: true,
        id: soId
      });
    }
    const doId = this.injector.get(ActivatedRoute).snapshot.queryParams['do'];
    if (doId) {
      return this.http.post('api/salestransfers/TransferDoToSinv', {
        _silent: true,
        id: doId
      });
    }
    return this.http.get('api/salesinvoices/new');
  }

  get(id: string): Observable<any> {
    return this.http.get(`api/salesinvoices/${id}`);
  }

  create(body: any) {
    return from(
      this.sqcSrv.check('SINV', this.form.value.itemDetails, true)
    ).pipe(
      switchMap(() => this.http.post('api/salesinvoices/create/', body)),
      catchError(() => throwError(() => new Error('User cancelled')))
    );
    //return this.http.post('api/salesinvoices/create/', body);
  }

  update(body: any) {
    return from(
      // Pass document ID for edit mode to correctly calculate available stock
      this.sqcSrv.check('SINV', this.form.value.itemDetails, this.qtyWasModify, this._data?.id)
    ).pipe(
      tap(() => {
        this.qtyWasModify = false;
      }),
      switchMap(() =>
        this.http.post('api/salesinvoices/update/', body).pipe(
          catchError(error => {
            if (error.error?.code === 'NEEDS_CONFIRMATION') {
              return new Observable(subscriber => {
                this.modalService.confirm({
                  nzTitle: 'Knock-off Confirmation',
                  nzContent: `${error.error.message}<br><br>Are you sure you want to save?`,
                  nzOkText: 'Proceed',
                  nzOkType: 'primary',
                  nzOnOk: () => {
                    this.http
                      .post(
                        'api/salesinvoices/update/?confirmedForKnockOff=true',
                        body
                      )
                      .subscribe({
                        next: res => subscriber.next(res),
                        error: err => subscriber.error(err)
                      });
                  },
                  nzCancelText: 'Cancel',
                  nzOnCancel: () =>
                    subscriber.error(new Error('Update canceled by user.'))
                });
              });
            }
            return throwError(() => error);
          })
        )
      ),
      catchError(() => throwError(() => new Error('User cancelled')))
    );
  }

  openDoc(line: any) {
    this.docNav.openDoc(line.paymentDocType, line.paymentDocCode);
  }

  override onAction(action: string, data: any): Promise<boolean> {
    const type = action?.toLowerCase();
    switch (action) {
      case 'save':
        return this.handleSave();
      case 'void':
        return new Promise((resolve, reject) => {
          this.confirmClearKnockoffIfAny(data.id, action)
            .then(() => resolve(true))
            .catch(() => reject(false));
        });
      default:
        return super.onAction(`${type}`, data);
    }
  }

  confirmClearKnockoffIfAny(id: any, action: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http
        .post(
          'api/SalesInvoices/IsBatchKnockedoff',
          { ids: [id] },
          { _silent: true }
        )
        .subscribe((res: any) => {
          if (res === true) {
            const modalRef = this.modalService.confirm({
              nzTitle: `${action === 'delete' ? 'Delete' : 'Cancel'} Record?`,
              nzContent:
                '<b>Sales Invoice(s) is already knocked-off. Matched data will be cleared.</b>',
              nzOkText: 'Proceed',
              nzOkType: 'primary',
              nzOnOk: () => {
                resolve(true);
                modalRef.destroy();
              },
              nzCancelText: 'Cancel',
              nzOnCancel: () => {
                reject();
                modalRef.destroy();
              }
            });
          } else {
            resolve(true);
          }
        });
    });
  }

  void(body: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post('api/salesinvoices/void', body).subscribe({
        next: (res: any) => {
          resolve(res);
        },
        error: () => {
          reject();
        }
      });
    });
  }

  devoid(id: string) {
    return this.http.post(`api/salesinvoices/devoid`, { id });
  }

  setBounced() {
    return EMPTY;
  }

  clearChq() {
    return EMPTY;
  }

  setPDC() {}

  setAndSavePDC() {
    return EMPTY;
  }

  setAndSaveUnmarkPDC() {
    return EMPTY;
  }

  transferToSo() {}

  transferToDo() {}

  transferToSinv() {}

  transferToCs() {}

  transferToCsr() {}

  transferToPo() {}

  transferToGrn() {}

  transferToPinv() {}

  transferToCp() {}

  transferToCpf() {}

  doDocTransfer(docType: string) {
    const docIds = this.transferSrv.outstandingDocs
      .filter(x => x.isTransfer)
      .map(x => x.docId);

    // if (docIds.length === 0) {
    //   this.msg.error('No transaction was chosen');
    //   return EMPTY;
    // }

    return this.http.post(`api/SalesTransfers/TransferDocsToSinv`, {
      docType: docType,
      fromDocIds: docIds,
      salesInvoice: this.form.value
    });
  }

  delete(id: string) {
    return new Promise((resolve, reject) => {
      this.confirmClearKnockoffIfAny(id, 'delete')
        .then(() => {
          this.http
            .delete(`api/salesinvoices`, null, { body: { ids: [id] } })
            .subscribe({
              next: (res: any) => {
                resolve(res);
              },
              error: () => {
                reject();
              }
            });
        })
        .catch(() => {
          reject();
        });
    });
  }

  initForm(data: any) {
    this.itemDetails.clear();
    const isNew = data.id === GUID_EMPTY;
    const lineCount =
      (data.itemDetails?.length ?? 0 > 0) ? data.itemDetails.length : 0; // Start with 0 lines for new documents
    this.addLines(lineCount);
    // Initialize BIR 2307 Collected fields
    if (data) {
      this.form.controls.isBIR2307Collected.setValue(
        data.isBir2307Collected || false
      );
      this.form.controls.biR2307CollectedDate.setValue(
        data.bir2307CollectedDate ? new Date(data.bir2307CollectedDate) : null
      );
    }
    // For new documents, add a line after a short delay to ensure form is ready
    if (isNew && lineCount === 0) {
      setTimeout(() => {
        this.addLines(1);
      }, 50);
    }

    if (this.isEInvEnabled) {
      // disable if submitted
      if (
        !this.editing &&
        data.uin &&
        data.eInvoiceStatus &&
        data.eInvoiceStatus !== 'Invalid'
      ) {
        this.dimmedDelete = true;
      }

      // disable if added as consolidated
      if (!this.editing && data.consolidatedDocCode) {
        this.dimmedDelete = true;
      }
    }
  }

  createForm() {
    const form = this.fb.group({
      id: [<any>null, []],
      docCode: ['', [Validators.required, Validators.maxLength(50)]],
      docDate: [new Date(), [Validators.required]],
      postDate: [new Date(), []],
      taxDate: [new Date(), [Validators.required]],
      customerId: ['', [Validators.required]],
      customer: [<any>null, [Validators.required]],
      currencyId: ['', [Validators.required]],
      currency: [<any>null, [Validators.required]],
      currencyRate: [1, [Validators.required]],
      termId: ['', [Validators.required]],
      term: [<any>null, [Validators.required]],
      agentId: ['', []],
      agent: [<any>null, []],
      wVatCodeId: ['', []],
      wVatCode: [<any>null, []],
      billingAddress: ['', [Validators.maxLength(400)]],
      referenceNo: ['', [Validators.maxLength(50)]],
      description: ['', [Validators.maxLength(250)]],
      attention: ['', [Validators.maxLength(200)]],
      ourDONumber: ['', [Validators.maxLength(3000)]],
      deliveryTerm: ['', [Validators.maxLength(50)]],
      doBranchCode: ['', [Validators.maxLength(20)]],
      doAddress: ['', [Validators.maxLength(400)]],
      doBranchName: ['', [Validators.maxLength(250)]],
      doContact: ['', [Validators.maxLength(200)]],
      doPhoneNo: ['', [Validators.maxLength(100)]],
      doFaxNo: ['', [Validators.maxLength(100)]],
      dotinType: [1, []],
      dotinNo: ['', [Validators.maxLength(14)]],
      doZipCode: ['', [Validators.maxLength(20)]],
      doCity: ['', [Validators.maxLength(50)]],
      doState: ['', [Validators.maxLength(100)]],
      doCountry: ['', [Validators.maxLength(100)]],
      identificationNo: ['', []],
      isTaxInclusive: [false, []],
      isDeferredTax: [false, []],
      notes: ['', [Validators.maxLength(5000)]],
      ref1: ['', [Validators.maxLength(50)]],
      ref2: ['', [Validators.maxLength(50)]],
      ref3: ['', [Validators.maxLength(50)]],
      ref4: ['', [Validators.maxLength(50)]],
      ref5: ['', [Validators.maxLength(50)]],
      remark1: ['', [Validators.maxLength(250)]],
      remark2: ['', [Validators.maxLength(250)]],
      remark3: ['', [Validators.maxLength(250)]],
      remark4: ['', [Validators.maxLength(250)]],
      remark5: ['', [Validators.maxLength(250)]],
      projectId: [<any>null, []],
      project: [<any>null, []],
      stockLocationId: [<any>null, []],
      stockLocation: [<any>null, []],
      isBIR2307Collected: [false, []],
      biR2307CollectedDate: [<any>null, []],
      totalAmount: [<number>0, []],
      totalServiceAmount: [0, []],
      discountTotalAmount: [<number>0, []],
      subtotalAmount: [<number>0, []],
      taxTotalAmount: [<number>0, []],
      rentalLeaseExemptedTaxTotalAmount: [<number>0, []],
      wTaxTotalAmount: [<number>0, []],
      wVatTotalAmount: [<number>0, []],
      taxExclusiveTotalAmount: [<number>0, []],
      isRounding: [false, []],
      roundingAdjustment: [<number>0, []],
      netTotalAmount: [<number>0, []],
      outstandingAmount: [<number>0, []],
      documentStatus: [<any>null, []],
      itemDetails: this.fb.array([this.createLine()]),
      isGovCat: [false, []],
      billingPeriodFrom: [new Date(), []],
      billingPeriodTo: [new Date(), []],
      frequencyOfBilling: ['', [Validators.maxLength(2)]],
      customsRefNo: ['', [Validators.maxLength(300)]],
      incoterms: ['', [Validators.maxLength(3)]],
      issuerMsicCodeId: ['', []],
      issuerMsicCode: [<any>null, []],
      detailsTaxExemption: ['', [Validators.maxLength(300)]],
      tinNumber: ['', [Validators.maxLength(14)]],
      //Einvoice field
      uin: ['', []],
      eInvoiceStatus: ['', []],
      eInvoiceControl: [0],
      customerName: ['', [Validators.maxLength(250)]],
      customerPhone: ['', [Validators.maxLength(100)]],
      email: ['', [Validators.email, Validators.maxLength(300)]],
      customerTinType: [<any>null, []],
      registrationNo: ['', [Validators.maxLength(40)]],
      salesTaxRegNo: ['', [Validators.maxLength(35)]],
      serviceTaxRegNo: ['', [Validators.maxLength(35)]],
      address1: ['', [Validators.maxLength(300)]],
      address2: ['', [Validators.maxLength(300)]],
      address3: ['', [Validators.maxLength(100)]],
      address4: ['', [Validators.maxLength(100)]],
      zipCode: ['', [Validators.maxLength(20)]],
      city: ['', [Validators.maxLength(50)]],
      state: ['', [Validators.maxLength(100)]],
      country: ['', [Validators.maxLength(100)]],
      //version: ['', [Validators.maxLength(5)]],
      //eInvoiceStatus: ['', [Validators.maxLength(10)]],
      //eInvoiceType: ['', [Validators.maxLength(2)]],
      //uin: ['', [Validators.maxLength(100)]],
      //digitalSignature: ['', [Validators.maxLength(100)]],
      //submissionDate: [new Date(), []],
      //validateOn: [new Date(), []],
      ftaInfo: ['', [Validators.maxLength(300)]],
      certifiedExporterNo: ['', [Validators.maxLength(300)]],
      customsFormNo2: ['', [Validators.maxLength(12)]],
      originCountry: ['', [Validators.maxLength(3)]],
      otherCharges: ['', [Validators.maxLength(300)]],
      amountExemptedFromTax: [<number>0, []],
      taxExemptionTotalAmount: [<number>0, []],
      enableDeferredVat: [true, []]
    });
    form.controls.agent.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.form.controls.agentId.setValue(val?.id);
    });
    form.controls.term.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.form.controls.termId.setValue(val?.id);
    });

    form.controls.issuerMsicCode.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.form.controls.issuerMsicCodeId.setValue(val?.id);
    });

    form.controls.docDate.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      form.controls.taxDate.setValue(val);

      this.observeChanges = false;
      form.controls.postDate.setValue(val);
      this.observeChanges = true;
    });

    form.controls.postDate.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      // if post date is earlier than doc date, set doc date to post date
      val = new Date(val!);
      const isEarlier =
        new Date(val!.toDateString()) <
        new Date(this.form.controls.docDate.value!.toDateString());

      if (isEarlier === true) {
        this.form.controls.docDate.setValue(val);
      }
    });

    form.controls.customer.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      if (val && val.id > 0) {
        form.controls.customerId.setValue(val.id);
        form.controls.customerName.setValue(val.name);
        form.controls.wVatCodeId.setValue(val?.defaultWVatCodeId);
        this.http
          .get(`api/customers/${val.id}/defaultvalues`)
          .subscribe((res: any) => {
            if (res) {
              form.patchValue({
                agent: res.agent,
                agentId: res.agent?.id,
                term: res.term,
                termId: res.term?.id,
                currency: res.currency,
                currencyId: res.currency.id,
                currencyRate: res.currency.defaultSalesRate,
                tinNumber: res.tinNumber,
                attention: res.contactPerson,
                billingAddress: this.formatAddress(res),
                doAddress: this.formatDoAddress(res),
                dotinType: res.tinType,
                dotinNo: res.tinNumber,
                doBranchCode: res.branchCode,
                doBranchName: res.branchName,
                doZipCode: res.doZipCode,
                doCity: res.city,
                doState: res.state,
                doCountry: res.country,
                doContact: res.doContactPerson,
                doPhoneNo: res.doPhoneNo,
                doFaxNo: res.faxNo1,
                identificationNo: res.registrationNo,
                isDeferredTax: res.isDeferredTax,
                wVatCode: res.defaultWVatCode,
                billingPeriodFrom: res.billingPeriodFrom,
                billingPeriodTo: res.billingPeriodTo,
                frequencyOfBilling: res.frequencyOfBilling,
                customerName: res.name,
                customerPhone: res.phoneNo1,
                email: res.email,
                customerTinType: res.tinType,
                registrationNo: res.registrationNo,
                salesTaxRegNo: res.salesTaxRegNo,
                serviceTaxRegNo: res.serviceTaxRegNo,
                address1: res.address1,
                address2: res.address2,
                address3: res.address3,
                address4: res.address4,
                zipCode: res.zipCode,
                city: res.city,
                state: res.state,
                country: res.country,
                detailsTaxExemption: res.salesTaxExemptionNo ?? null
              });
            }

            if (this.isEInvEnabled && this.form.controls.itemDetails.value) {
              this.form.controls.itemDetails.controls.forEach(line => {
                if (
                  line.controls.stockId.value &&
                  line.controls.stockId.value > 0
                ) {
                  this.http
                    .post(
                      'api/CustomerTaxExemptionItems/TaxExemptionDefaultValues?_silent=true',
                      {
                        customerId: val.id,
                        stockId: line.controls.stockId.value
                      }
                    )
                    .subscribe((taxExp: any) => {
                      if (taxExp) {
                        line.controls.taxCodeId.setValue(taxExp.taxCodeId);
                        line.controls.taxCode.setValue(taxExp.taxCode);
                        line.controls.taxExemptionNo.setValue(
                          taxExp.taxExemptionNo
                        );
                        line.controls.taxExemptionDetails.setValue(
                          taxExp.taxExemptionDetails
                        );
                        line.controls.taxExemptionRate.setValue(
                          taxExp.taxCode?.exemptionRate
                        );
                        line.controls.taxExemptionAmount.setValue(
                          this.calc.calcTaxExemptionAmount({
                            ...line.value,
                            taxExemptionRate: taxExp.taxCode?.exemptionRate
                          })
                        );
                      }
                    });
                }
              });
            }

            if (!this.isGovBirCat) {
              for (let line of this.itemDetails.controls) {
                line.patchValue({ wVatCode: null });
                this.recalcLine(line);
              }
              this.recalcHeader(form, this.itemDetails.value);
            }
          });
      } else {
        form.controls.customerId.setValue(null);
        form.controls.customerName.setValue(null);
        form.controls.billingPeriodFrom.setValue(null);
        form.controls.billingPeriodTo.setValue(null);
        form.controls.frequencyOfBilling.setValue(null);
        form.controls.detailsTaxExemption.setValue(null);
      }
    });

    form.controls.currency.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.form.controls.currencyId.setValue(val?.id);

      this.observeChanges = false;
      if (val.local) {
        this.form.controls.currencyRate.setValue(1);
      } else {
        this.form.controls.currencyRate.setValue(val?.defaultSalesRate);
      }
      for (let line of this.itemDetails.controls) {
        this.recalcLine(line);
      }
      this.recalcHeader(form, this.itemDetails.value);
      this.observeChanges = true;
    });

    form.controls.currencyRate.valueChanges.subscribe((_: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      for (let line of this.itemDetails.controls) {
        this.recalcLine(line);
      }
      this.recalcHeader(form, this.itemDetails.value);
      this.observeChanges = true;
    });

    form.controls.isTaxInclusive.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;

      this.observeChanges = false;
      for (let line of this.itemDetails.controls) {
        line.controls.isTaxInclusive.setValue(val);
        this.recalcLine(line);
      }
      this.recalcHeader(form, this.itemDetails.value);
      this.observeChanges = true;
    });

    form.controls.isRounding.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      this.recalcHeader(form, this.itemDetails.value);
      this.observeChanges = true;
    });

    form.controls.project.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      form.controls.projectId.setValue(val?.id);

      this.observeChanges = false;
      for (let line of this.itemDetails.controls) {
        if (line.controls.stockId.value) {
          line.controls.project.setValue(val);
          line.controls.projectId.setValue(val?.id);
        }
      }
      this.observeChanges = true;
    });

    form.controls.stockLocation.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      form.controls.stockLocationId.setValue(val?.id);

      this.observeChanges = false;
      if (val) {
        for (let line of this.itemDetails.controls) {
          if (line.controls.stockId.value) {
            line.controls.stockLocation.setValue(val);
            line.controls.stockLocationId.setValue(val?.id);
          }
        }
      }
      this.observeChanges = true;
    });

    form.controls.doCountry.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;

      if (this.isEInvEnabled) {
        if (val !== 'MY') {
          form.controls.doState.setValue('Not Applicable');
        } else {
          if (this.form.controls.doState.value === 'Not Applicable') {
            this.form.controls.doState.setValue(null);
          }
        }
      }
    });

    form.controls.eInvoiceControl.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.einvValidators.toggleFormValidators(this.form, val);
    });

    form.valueChanges.subscribe(() => {
      if (!this.observeChanges) return;
      this.modified = true;
    });
    this.userDefaultService.setControlRequired(form.get('agent')!, 'Agent');
    this.userDefaultService.setControlRequired(
      form.get('stockLocation')!,
      'Location'
    );
    this.userDefaultService.setControlRequired(form.get('project')!, 'Project');

    return form;
  }

  lineValueChanged = new Subject<{ line: FormGroup; controlName: string }>();

  createLine() {
    const line = this.fb.group({
      id: [GUID_EMPTY, []], // ✅ Use GUID_EMPTY for new items (not null)
      pos: [0, []],
      numbering: [<any>null, []],
      isBundled: [false, []],
      isSubItem: [false, []],
      bundleId: [<any>null, []],
      stockId: [<any>null, []],
      stock: [<any>null, []],
      uomId: [<any>null, []],
      uom: [<any>null, []],
      accountId: [<any>null, []],
      account: [<any>null, []],
      description: ['', [Validators.maxLength(250)]],
      docFurtherDescription: ['', [Validators.maxLength(999999)]],
      referenceNo: ['', [Validators.maxLength(50)]],
      unitPrice: [0, []],
      qty: [0, []],
      amount: [0, []],
      ref1: ['', [Validators.maxLength(200)]],
      ref2: ['', [Validators.maxLength(200)]],
      dateRef1: [<any>null, []],
      dateRef2: [<any>null, []],
      taxCodeId: [<any>null, []],
      taxCode: [<any>null, []],
      taxRate: [0, []],
      rentalLeaseExemptedRate: [0, []],
      wTaxCodeId: [<any>null, []],
      wTaxCode: [<any>null, []],
      wTaxRate: [0, []],
      wVatCode: [<any>null, []],
      wVatCodeId: [<any>null, []],
      wVatRate: [0, []],
      wVatAmount: [0, []],
      projectId: [<any>null, []],
      project: [<any>null, []],
      stockLocationId: [<any>null, []],
      stockLocation: [<any>null, []],
      tariffCodeId: [<any>null, []],
      tariffCode: [<any>null, []],
      isTaxInclusive: [false, []],
      subAmount: [0, []],
      discount: ['', [Validators.maxLength(50)]],
      discountAmount: [0, []],
      taxAmount: [0, []],
      rentalLeaseExemptedTaxAmount: [0, []],
      taxAmountAdjustment: [0, []],
      wTaxAmount: [0, []],
      wTaxAmountAdjustment: [0, []],
      taxExclusiveAmount: [0, []],
      netAmount: [0, []],
      serviceAmount: [0, []],
      amountExemptedTax: [0, []], // Amount Exempted from Tax
      taxExemptionRate: [0, []],
      taxExemptionAmount: [0, []], // Exempted Tax Amount
      taxExemptionNo: ['', [Validators.maxLength(20)]],
      taxExemptionDetails: ['', [Validators.maxLength(300)]],
      classification: ['', [Validators.maxLength(3)]],
      transferFromQuotationDetailId: [<any>null, []],
      transferFromQuotationDetail: [<any>null, []],
      transferFromSalesOrderDetailId: [<any>null, []],
      transferFromSalesOrderDetail: [<any>null, []],
      transferFromDeliveryOrderDetailId: [<any>null, []],
      transferFromDeliveryOrderDetail: [<any>null, []]
    });

    // Select stock
    line.controls.stock.valueChanges.subscribe((stock: any) => {
      if (!this.observeChanges) return;
      this.bundleHelper.prepareLineForStockChange(this.itemDetails, line);

      if (stock) {
        const headerIndex = this.itemDetails.controls.indexOf(line);
        if (this.bundleHelper.isBundleStock(stock)) {
          const master = this.form.value;
          const transactionDate = master.docDate
            ? formatDate(master.docDate, 'yyyy-MM-dd', 'en-US')
            : null;
          this.observeChanges = false;
          line.patchValue({
            stockId: stock.id,
            description: stock.name,
            qty: line.controls.qty.value || 1,
            isTaxInclusive: master.isTaxInclusive,
            isBundled: true,
            isSubItem: false,
            bundleId: null
          });
          this.bundleHelper
            .expandBundleSelection({
              formArray: this.itemDetails,
              headerLine: line,
              headerIndex,
              stock,
              purchase: false,
              currencyRate: master.currencyRate,
              customerId: master.customerId,
              transactionDate,
              createLine: () => this.createLine(),
              recalcLine: l => this.recalcLine(l)
            })
            .then(() => {
              this.bundleHelper.updateBundleHeaderFromSubItems(
                this.itemDetails,
                line
              );
              this.reorderNumbering();
              this.recalcHeader(this.form, this.itemDetails.value);
              this.observeChanges = true;
            });
          this.lineValueChanged.next({ line, controlName: 'stock' });
          return;
        }

        const master = this.form.value;

        let defaultValues = {
          stockId: stock.id,
          description: stock.name,
          classification: stock.salesClassification,
          isRequiredEInvoice: stock.isRequiredEInvoice,
          qty: 1,
          project: master.project,
          projectId: master.projectId,
          isTaxInclusive: master.isTaxInclusive
        };

        // assign default values
        this.http
          .get('api/stocks/DefaultValues', {
            stockId: stock.id,
            currencyRate: master.currencyRate,
            purchase: false,
            customerId: master.customerId,
            qty: line.controls.qty.value || 1,
            transactionDate: master.docDate
              ? formatDate(master.docDate, 'yyyy-MM-dd', 'en-US')
              : null
          })
          .subscribe((res: any) => {
            this.observeChanges = false;
            if (res) {
              stock.unitPrice = res.unitPrice;

              Object.assign(defaultValues, {
                ...res,
                account: res.salesAccount,
                accountId: res.salesAccount?.id,
                tariffCode: res.tariffCode,
                tariffCodeId: res.tariffCode?.id,
                classification: res.salesClassification,
                isRequiredEInvoice: res.isRequiredEInvoice,
                docFurtherDescription: res.furtherDescription,
                doPhoneNo: res.doPhoneNo,
                doFaxNo: res.doFaxNo,
                stockLocation: master.stockLocation ?? res.stockLocation,
                stockLocationId: master.stockLocationId ?? res.stockLocation?.id
              });

              // If GST module is enabled, get the default tax code
              if (this.acl.can('m1.512')) {
                Object.assign(defaultValues, {
                  taxCode: res.outputTaxCode,
                  taxCodeId: res.outputTaxCode?.id,
                  taxRate: res.outputTaxCode ? res.outputTaxCode.rate : 0,
                  taxExemptionRate: res.outputTaxCode
                    ? res.outputTaxCode.exemptionRate
                    : 0,
                  rentalLeaseExemptedRate:
                    res.outputTaxCode?.rlServiceTaxExemptedRate ?? 0
                });
              }

              if (
                this.isEInvEnabled &&
                this.form.controls.customerId.value &&
                this.form.controls.customerId.value !== '0'
              ) {
                this.http
                  .post(
                    'api/CustomerTaxExemptionItems/TaxExemptionDefaultValues?_silent=true',
                    {
                      customerId: this.form.controls.customerId.value,
                      stockId: stock.id
                    }
                  )
                  .subscribe((taxExp: any) => {
                    if (taxExp) {
                      line.controls.taxCodeId.setValue(taxExp.taxCodeId);
                      line.controls.taxCode.setValue(taxExp.taxCode);
                      line.controls.taxExemptionNo.setValue(
                        taxExp.taxExemptionNo
                      );
                      line.controls.taxExemptionDetails.setValue(
                        taxExp.taxExemptionDetails
                      );
                      line.controls.taxExemptionRate.setValue(
                        taxExp.taxCode?.exemptionRate
                      );
                    }
                  });
              }

              // ✅ CRITICAL: Preserve the original item ID before patching
              // This ensures existing items keep their database GUID when stock is changed
              const originalId = line.controls.id.value;
              line.patchValue(defaultValues);
              line.controls.id.setValue(originalId); // Restore the original ID

              this.recalcLine(line);
              this.recalcHeader(
                this.form,
                this.form.controls.itemDetails.value
              );
            }

            this.reorderNumbering();
            this.observeChanges = true;
          });

        if (this.form.controls.wVatCode.value !== null) {
          line.controls.wVatCode.setValue(this.form.controls.wVatCode.value);
          line.controls.wVatCodeId.setValue(
            this.form.controls.wVatCodeId.value
          );
          line.controls.wVatRate.setValue(
            this.form.controls.wVatCode.value.rate
          );
        } else {
          line.patchValue({ wVatRate: 0, wVatAmount: 0, wVatCodeId: null });
        }
        // implement existing row have value and auto next row
        // add new line if the next line doesnt exists
        var curPos = line.value.pos;
        if (curPos) {
          // If the current position is the last line, add a new line
          if (curPos >= this.itemDetails.value.length) this.addLines(1);
        }
      } else {
        this.observeChanges = false;
        line.patchValue({
          numbering: null,
          description: '',
          docFurtherDescription: '',
          referenceNo: '',
          taxCode: null,
          taxCodeId: null,
          tariffCode: null,
          tariffCodeId: null,
          taxRate: 0,
          rentalLeaseExemptedRate: 0,
          amount: 0,
          ref1: '',
          ref2: '',
          dateRef1: null,
          dateRef2: null,
          wTaxCode: null,
          wTaxCodeId: null,
          wTaxRate: 0,
          project: null,
          projectId: null,
          stockLocation: null,
          stockLocationId: null,
          account: null,
          accountId: null,
          stock: null,
          stockId: null,
          uomId: null,
          uom: null,
          qty: 0,
          unitPrice: 0,
          subAmount: 0,
          taxAmount: 0,
          rentalLeaseExemptedTaxAmount: 0,
          taxAmountAdjustment: 0,
          wTaxAmount: 0,
          wTaxAmountAdjustment: 0,
          taxExclusiveAmount: 0,
          netAmount: 0,
          wVatCodeId: null,
          wVatAmount: 0,
          wVatRate: 0,
          amountExemptedTax: 0,
          taxExemptionRate: 0,
          taxExemptionAmount: 0,
          taxExemptionNo: '',
          taxExemptionDetails: '',
          classification: null
        });
        this.reorderNumbering();
        this.recalcLine(line);
        this.recalcHeader(this.form, this.itemDetails.value);
        this.observeChanges = true;
      }
      this.lineValueChanged.next({ line, controlName: 'stock' });
    });

    line.controls.account.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      if (val && val.id !== GUID_EMPTY) {
        line.controls.accountId.setValue(val.id);
      }
      this.lineValueChanged.next({ line, controlName: 'account' });
    });

    line.controls.qty.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      // if (line.controls.transferFromQuotationDetail) {
      //   const remainingQty = +line.controls.transferFromQuotationDetail.value['remainingQty'];
      //   if (+val! > remainingQty) {
      //     line.controls.qty.setValue(remainingQty);
      //   }
      // }
      line.value.qty = val;
      this.qtyWasModify = true;

      const isBundleHeader =
        line.controls.isBundled?.value === true &&
        line.controls.isSubItem?.value !== true;
      if (isBundleHeader) {
        this.bundleHelper.syncBundleSubItemQty(this.itemDetails, line);
        this.itemDetails.controls.forEach(subLine => {
          const fg = subLine as FormGroup;
          if (fg.get('bundleId')?.value === line.get('id')?.value) {
            if (fg.get('isSubItem')?.value === true) {
              this.recalcLine(fg);
            }
          }
        });
        this.bundleHelper.updateBundleHeaderFromSubItems(
          this.itemDetails,
          line
        );
        this.recalcHeader(this.form, this.itemDetails.value);
        this.observeChanges = true;
        return;
      }

      // Recalculate price based on quantity (for price rules with MinQty)
      // Only if price rule feature is enabled
      if (this.acl.can('pricerule.enabled')) {
        const master = this.form.value;
        const stock = line.controls.stock.value;
        if (stock && master.customerId && val > 0) {
          this.http
            .get('api/stocks/DefaultValues', {
              stockId: stock.id,
              currencyRate: master.currencyRate,
              purchase: false,
              customerId: master.customerId,
              qty: val,
              transactionDate: master.docDate
                ? formatDate(master.docDate, 'yyyy-MM-dd', 'en-US')
                : null
            })
            .subscribe((res: any) => {
              if (res && res.unitPrice !== line.controls.unitPrice.value) {
                line.controls.unitPrice.setValue(res.unitPrice);
              }
            });
        }
      }

      this.recalcLine(line);
      this.recalcHeader(this.form, this.itemDetails.value);
      this.observeChanges = true;
    });

    line.controls.uom.valueChanges.subscribe((uom: any) => {
      if (!this.observeChanges) return;
      line.controls.uomId.setValue(uom?.id);
      this.observeChanges = false;
      const master = this.form.value;
      const stock = line.controls.stock.value;

      const finishUomChange = () => {
        this.recalcLine(line);
        this.recalcHeader(this.form, this.itemDetails.value);
        this.observeChanges = true;
        this.lineValueChanged.next({ line, controlName: 'uom' });
      };

      const isBundleHeader =
        line.controls.isBundled?.value === true &&
        line.controls.isSubItem?.value !== true;
      if (isBundleHeader) {
        if (uom && stock) {
          let listPrice: number =
            uom.salesPrice > 0
              ? <number>uom.salesPrice
              : (stock.unitPrice || 0) * uom.rate;
          if (!listPrice) {
            listPrice = 0;
          }
          let unitPrice = master.currencyRate
            ? listPrice / master.currencyRate
            : listPrice;
          if (!unitPrice) {
            unitPrice = 0;
          }
          line.controls.unitPrice.setValue(unitPrice);
        }
        finishUomChange();
        return;
      }

      if (uom && stock) {
        this.http
          .get('api/stocks/DefaultValues', {
            stockId: stock.id,
            currencyRate: master.currencyRate,
            purchase: false,
            customerId: master.customerId,
            qty: line.controls.qty.value || 1,
            transactionDate: master.docDate
              ? formatDate(master.docDate, 'yyyy-MM-dd', 'en-US')
              : null,
            uomId: uom.id
          })
          .subscribe({
            next: (res: any) => {
              if (res && res.unitPrice != null) {
                line.controls.unitPrice.setValue(res.unitPrice);
              }
              finishUomChange();
            },
            error: () => {
              let listPrice: number =
                uom.salesPrice > 0
                  ? <number>uom.salesPrice
                  : (stock.unitPrice || 0) * uom.rate;
              if (!listPrice) {
                listPrice = 0;
              }
              let unitPrice = master.currencyRate
                ? listPrice / master.currencyRate
                : listPrice;
              if (!unitPrice) {
                unitPrice = 0;
              }
              line.controls.unitPrice.setValue(unitPrice);
              finishUomChange();
            }
          });
        return;
      }

      finishUomChange();
    });

    line.controls.unitPrice.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.value.unitPrice = val;
      this.recalcLine(line);
      this.recalcHeader(this.form, this.itemDetails.value);
      this.observeChanges = true;
    });

    line.controls.discount.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.value.discount = val;
      this.recalcLine(line);
      this.recalcHeader(this.form, this.itemDetails.value);
      line.patchValue({ discountAmount: line.value.discountAmount });
      this.observeChanges = true;
    });

    line.controls.taxCode.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.controls.taxCodeId.setValue(val?.id);
      line.controls.taxRate.setValue(val?.rate ?? 0);
      line.controls.taxExemptionRate.setValue(val?.exemptionRate ?? 0);
      line.controls.rentalLeaseExemptedRate.setValue(
        val?.rlServiceTaxExemptedRate ?? 0
      );
      if (!val) {
        line.controls.taxAmountAdjustment.setValue(0);
      }
      this.recalcLine(line);
      this.recalcHeader(this.form, this.itemDetails.value);
      this.observeChanges = true;
      this.lineValueChanged.next({ line, controlName: 'taxCode' });
    });

    line.controls.taxExemptionRate.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.value.taxExemptionRate = val;
      this.recalcLine(line);
      this.recalcHeader(this.form, this.itemDetails.value);
      this.observeChanges = true;
      this.lineValueChanged.next({ line, controlName: 'taxExemptionRate' });
    });

    line.controls.wTaxCode.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.controls.wTaxCodeId.setValue(val?.id);
      line.controls.wTaxRate.setValue(val?.rate ?? 0);
      if (!val) {
        line.controls.wTaxAmountAdjustment.setValue(0);
      }
      this.recalcLine(line);
      this.recalcHeader(this.form, this.itemDetails.value);
      this.observeChanges = true;
      this.lineValueChanged.next({ line, controlName: 'wTaxCode' });
    });

    line.controls.wVatCode.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.controls.wVatCodeId.setValue(val?.id);
      line.controls.wVatRate.setValue(val?.rate ?? 0);
      this.recalcLine(line);
      this.recalcHeader(this.form, this.itemDetails.value);
      this.observeChanges = true;
    });

    line.controls.tariffCode.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.controls.tariffCodeId.setValue(val?.id);
      this.observeChanges = true;
      this.lineValueChanged.next({ line, controlName: 'tariffCode' });
    });

    line.controls.project.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      line.controls.projectId.setValue(val?.id);
      this.lineValueChanged.next({ line, controlName: 'project' });
    });

    line.controls.stockLocation.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      line.controls.stockLocationId.setValue(val?.id);
      this.lineValueChanged.next({ line, controlName: 'stockLocation' });
    });

    line.controls.amount.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.value.amount = val;
      this.recalcLine(line);
      this.recalcHeader(this.form, this.itemDetails.value);
      this.observeChanges = true;
    });

    line.controls.classification.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.controls.classification.setValue(val);
      this.observeChanges = true;
      this.lineValueChanged.next({ line, controlName: 'classification' });
    });

    line.controls.taxExemptionNo.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      line.controls.taxExemptionNo.setValue(val);
      this.observeChanges = true;
    });

    line.controls.taxAmountAdjustment.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      this.recalcLine(line);
      this.recalcHeader(this.form, this.itemDetails.value);
      this.observeChanges = true;
    });

    line.controls.wTaxAmountAdjustment.valueChanges.subscribe((val: any) => {
      if (!this.observeChanges) return;
      this.observeChanges = false;
      this.recalcLine(line);
      this.recalcHeader(this.form, this.itemDetails.value);
      this.observeChanges = true;
    });
    return line;
  }

  reorderNumbering() {
    let numbering = 1;
    for (let i = 0; i < this.itemDetails.length; i++) {
      const item = this.itemDetails.at(i);
      if (item.value.isSubItem === true) {
        item.patchValue({ numbering: null });
        continue;
      }
      if (item.value.stockId) {
        item.patchValue({ numbering: numbering });
        numbering++;
      }
    }
  }

  // updateDetailsTaxExemption() {
  //   const items = this.itemDetails.value;

  //   if (Array.isArray(items) && items.length > 0) {
  //     const taxExemptionItems = items
  //       .filter(o => o.stockId != null && o.taxExemptionNo != null)
  //       .map(o => o.taxExemptionNo?.trim())
  //       .filter(detail => detail);

  //     const dteValue = Array.from(new Set(taxExemptionItems));
  //     const value = dteValue.join(',');
  //     this.form.controls.detailsTaxExemption.setValue(value);
  //   } else {
  //     this.form.controls.detailsTaxExemption.setValue(null);
  //   }
  // }

  loadPaymentInfo(cdr?: any) {
    let docId = this.id;
    this.paymentInfoItems = [];
    this.paymentInfoLoading = true;

    if (docId) {
      this.http
        .get<any[]>(`api/salesinvoices/GetPaymentInfo?key=${docId}`)
        .subscribe({
          next: (res: any) => {
            // When payment info is loaded, set the outstanding amount instead of reload whole doc (to let the 'app-tranx-summary' component update correctly)
            // by calculating the total payment amount and subtracting it from the net total amount
            var paymentAmount = res.reduce(
              (acc: number, item: any) => acc + (item.paymentAmount ?? 0),
              0
            );
            var netTotal = this.form.controls.netTotalAmount.value ?? 0;
            var outstanding = netTotal - paymentAmount;
            this.form.controls.outstandingAmount.setValue(outstanding);

            this.paymentInfoItems = [...res];
            this.paymentInfoLoading = false;
            cdr?.detectChanges();
          },
          error: () => {
            this.paymentInfoLoading = false;
            cdr?.detectChanges();
          }
        });
    } else {
      this.paymentInfoLoading = false;
    }
    return (this.paymentInfoItems = []);
  }

  updateLatestRate() {
    this.updatingRate = true;
    this.http
      .post('api/currencies/latestrate?_silent=true', {
        currencyCode: this.form.controls.currency.value.code,
        isAR: true
      })
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.form.controls.currencyRate.setValue(res);
          }
          this.updatingRate = false;
        },
        error: () => (this.updatingRate = false)
      });
  }

  checkTaxExempted(lines: any[]) {
    const haveTaxExempt =
      this.isEInvEnabled &&
      lines.some(line => line.taxCode && line.taxCode.taxType == 2);
    const validators = haveTaxExempt
      ? [Validators.required, Validators.maxLength(300)]
      : [Validators.maxLength(300)];
    this.form.controls.detailsTaxExemption.setValidators(validators);
    this.form.controls.detailsTaxExemption.updateValueAndValidity({
      onlySelf: true
    });
  }

  addLines(count: number = 1) {
    const isTaxIncl = this.form.controls.isTaxInclusive.value;
    for (let i = 0; i < count; i++) {
      let line = this.createLine();
      this.observeChanges = false;
      line.patchValue({
        pos: this.itemDetails.length + 1,
        isTaxInclusive: isTaxIncl
      });
      this.einvValidators.toggleLineValidators(
        line,
        this.form.controls.eInvoiceControl.value
      );
      this.itemDetails.push(line);
      this.observeChanges = true;
    }
  }

  recalcLine(line: FormGroup) {
    line.controls['amount'].setValue(this.calc.calcAmount(line.value));
    line.controls['discountAmount'].setValue(
      this.calc.calcDiscount(line.value)
    );
    line.controls['subAmount'].setValue(this.calc.calcSubAmount(line.value));
    line.controls['taxAmount'].setValue(this.calc.calcTaxAmount(line.value));
    line.controls['rentalLeaseExemptedTaxAmount'].setValue(
      this.calc.calcRentalLeaseExemptedTaxAmount(line.value)
    );
    line.controls['wTaxAmount'].setValue(this.calc.calcWTaxAmount(line.value));
    if (line.controls['wVatRate'].value !== null) {
      line.controls['wVatAmount'].setValue(
        this.calc.calcWVatAmount(line.value)
      );
    }
    line.controls['taxExclusiveAmount'].setValue(
      this.calc.calcTaxExclusiveAmount(line.value)
    );
    line.controls['serviceAmount'].setValue(
      this.calc.calcServiceAmount(line.value)
    );
    line.controls['netAmount'].setValue(this.calc.calcNetAmount(line.value));
    line.controls['amountExemptedTax'].setValue(
      this.calc.calcAmountExemptedTax(line.value)
    );
    line.controls['taxExemptionAmount'].setValue(
      this.calc.calcTaxExemptionAmount(line.value)
    );
    this.bundleHelper.updateBundleHeaderFromLine(this.itemDetails, line);
  }

  recalcHeader(header: FormGroup, lines: any[]) {
    this.form.controls.totalAmount.setValue(
      this.calc.calcInventoryTotalAmount(lines)
    );
    this.form.controls.discountTotalAmount.setValue(
      this.calc.calcDiscountTotalAmount(lines)
    );
    this.form.controls.subtotalAmount.setValue(
      this.calc.calcSubtotalAmount(lines)
    );
    this.form.controls.taxTotalAmount.setValue(
      this.calc.calcTaxTotalAmount(lines)
    );
    this.form.controls.rentalLeaseExemptedTaxTotalAmount.setValue(
      this.calc.calcRentalLeaseExemptedTaxTotalAmount(lines)
    );
    this.form.controls.wTaxTotalAmount.setValue(
      this.calc.calcWTaxTotalAmount(lines)
    );
    this.form.controls.wVatTotalAmount.setValue(
      this.calc.calcWVatTotalAmount(lines)
    );
    this.form.controls.taxExclusiveTotalAmount.setValue(
      this.calc.calcTaxExclusiveTotalAmount(lines)
    );
    this.form.controls.totalServiceAmount.setValue(
      this.calc.calcTotalServiceAmount(lines)
    );
    if (this.form.controls.isRounding.value) {
      const total = this.calc.calcNetTotalAmount(lines);
      const rounded = this.calc.frontEndRounding(total);
      this.form.controls.roundingAdjustment.setValue(rounded - total);
      this.form.controls.netTotalAmount.setValue(rounded);
    } else {
      this.form.controls.netTotalAmount.setValue(
        this.calc.calcNetTotalAmount(lines)
      );
    }
    this.form.controls.amountExemptedFromTax.setValue(
      this.calc.calcTotalAmountExemptedTax(lines)
    );
    this.form.controls.taxExemptionTotalAmount.setValue(
      this.calc.calcTotalTaxExemptionAmount(lines)
    );
  }

  populateDOAddress(data: any) {
    // this.form.controls.doBranchCode.reset();
    // this.form.controls.doBranchName.reset();
    // this.form.controls.doAddress.reset();
    // this.form.controls.doContact.reset();
    // this.form.controls.doPhoneNo.reset();
    // this.form.controls.doFaxNo.reset();

    // if data have been selected
    if (data) {
      this.form.controls.doBranchCode.setValue(data.branchCode);
      this.form.controls.doBranchName.setValue(data.branchName);
      this.form.controls.doAddress.setValue(this.formatAddress(data));
      this.form.controls.dotinType.setValue(data.tinType);
      this.form.controls.dotinNo.setValue(data.tinNumber);
      this.form.controls.identificationNo.setValue(data.registrationNo);
      this.form.controls.doZipCode.setValue(data.zipCode);
      this.form.controls.doCity.setValue(data.city);
      this.form.controls.doState.setValue(data.state);
      this.form.controls.doCountry.setValue(data.country);
      this.form.controls.doContact.setValue(data.contactPerson);
      this.form.controls.doPhoneNo.setValue(data.phoneNo);
      this.form.controls.doFaxNo.setValue(data.faxNo);
    } else {
      // if data have not been selected,so get default value from customer api
      this.http
        .get(
          `api/customers/${this.form.controls.customer.value.id}/defaultvalues`
        )
        .subscribe((res: any) => {
          this.customer = res;
          this.form.patchValue({
            doBranchCode: res.branchCode,
            doBranchName: res.branchName,
            doAddress: this.formatDoAddress(res),
            dotinType: res.tinType,
            dotinNo: res.tinNumber,
            identificationNo: res.registrationNo,
            doZipCode: res.doZipCode,
            doCity: res.city,
            doState: res.state,
            doCountry: res.country,
            doContact: res.doContactPerson,
            doPhoneNo: res.doPhoneNo,
            doFaxNo: res.faxNo1
          });
        });
    }
  }

  updateCustInfo(data: any) {
    this.form.controls.customerName.setValue(data.customerName);
    this.form.controls.tinNumber.setValue(data.tinNumber);
    this.form.controls.registrationNo.setValue(data.registrationNo);
    this.form.controls.customerPhone.setValue(data.customerPhone);
    this.form.controls.email.setValue(data.email);
    this.form.controls.salesTaxRegNo.setValue(data.salesTaxRegNo);
    this.form.controls.serviceTaxRegNo.setValue(data.serviceTaxRegNo);
    this.form.controls.customerTinType.setValue(data.customerTinType);
    this.form.controls.zipCode.setValue(data.zipCode);
    this.form.controls.city.setValue(data.city);
    this.form.controls.state.setValue(data.state);
    this.form.controls.country.setValue(data.country);
    this.form.controls.address1.setValue(data.address1);
    this.form.controls.address2.setValue(data.address2);
    this.form.controls.address3.setValue(data.address3);
    this.form.controls.address4.setValue(data.address4);
  }

  private formatDoAddress(res: any): string {
    let addrArr = [
      res.doAddress1,
      res.doAddress2,
      res.doAddress3,
      res.doAddress4,
      res.doZipCode
    ];
    addrArr = addrArr.filter(function (val) {
      return val != null && val !== '';
    });
    let addr = addrArr.join('\r\n');
    return addr;
  }

  private handleSave(): Promise<boolean> {
    // Get deposit info
    if (this.componentRef) {
      const depositData = this.componentRef.getDepositRecords();
      if (depositData) {
        var processedData = depositData.map((item: any) => {
          if (item.id) return item;
          return {
            ...item,
            customerId: this.form.value.customerId,
            customer: this.form.value.customer,
            customerName: this.form.value.customerName,
            agentId: this.form.value.agentId,
            agent: this.form.value.agent
          };
        });

        this._data.depositInfo = {
          ...this._data.depositInfo,
          depositRecords: processedData
        };
      }
    }

    return this.vppSrv.check(this.form.value.docDate);
  }
}
